import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Text, Icon, Field } from '@ontruck/orc';
import DocsCarrierInfo from 'containers/Docs/DocsCarrierInfo';
import Select from 'react-select-v3';
import { injectIntl } from 'react-intl';
import DateField from 'components/DateField';
import { reactSelectStyles } from 'utils/style';
import { Loading } from 'components/Loading';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Checkbox } from '@ontruck/orc';
import DocsContext from 'containers/Docs/context/DocsContext';
import DocsAlert from 'containers/Docs/DocsAlert';
import DocsRejectModal from 'containers/Docs/DocsRejectModal';
import moment from 'moment';
import config from 'config';
import * as http from 'utils/http';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { useSelector } from 'react-redux';

const t = {
  docType: 'Document Type',
  validUntil: 'Valid Until',
  discard: 'Discard document',
  validate: 'Validate document',
  createCarrier: 'Create carrier',
  error: 'There was an error fetching the document',
  dateError: 'Should be a date from the future',
  rejectReasonTitle: 'Please enter a reject reason',
  rejectReasonLabel: 'Reject Reason',
  cancel: 'Cancel',
  reject: 'Discard',
  downloadDoc: 'Download document',
  previewDoc: 'Document Preview',
  previewError: 'This document has no preview available.',
  notifyCarrier: 'Notify carrier',
  plateNumber: 'Plate number',
  full_name: 'Driver',
  status: 'Status',
  number_of_pages: 'Pages',
  notOwner: 'Only the owner can validate or discard documents.',
};

const bankOwnershipCertificateLabel = 'Bank ownership certificate';
const insuranceLabel = 'Insurance';

const DocsTaskDetail = ({ intl, className }) => {
  const { docId } = useParams();
  const history = useHistory();

  const {
    loadingTaskDetail,
    error,
    taskDetail,
    fetchTaskDetail,
    fetchDocumentTypes,
    documentTypes,
    loadingDocTypes,
    postRejectDocument,
    postValidateDocument,
    resetState,
  } = useContext(DocsContext);

  const [selectedDocType, setSelectedDocType] = useState(null);
  const [docTypeOptions, setDocTypeOptions] = useState([]);
  const [validationError, setValidationError] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [ibanNumber, setIbanNumber] = useState(null);
  const [insuranceCoverage, setInsuranceCoverage] = useState(null);
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [notifyCarrier, setNotifyCarrier] = useState(true);
  const [loading, setLoading] = useState(false);
  const me = useSelector((state) => state.me);

  const handleImageError = () => {
    setPreviewError(true);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleShowModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleChangeDocType = useCallback(
    async (selectedOption) => {
      setSelectedDocType(selectedOption);
      await http.patch(`${config.BASE_URL}/api/dms/document_types/`, {
        document_type_id: selectedOption.value,
        document_id: docId,
      });
      await fetchTaskDetail(docId);
    },
    [docId, fetchTaskDetail]
  );

  const generateOptions = useCallback(() => {
    if (!documentTypes) return [];
    setDocTypeOptions(
      documentTypes
        .filter((docType) => docType.entity_type === taskDetail?.metadata?.entity_type)
        .map((docType) => {
          return {
            value: docType.id,
            label: docType.label,
          };
        })
    );
  }, [documentTypes, taskDetail]);

  const checkDateIsFromFuture = useCallback((date) => {
    return moment().isBefore(moment(date), 'day');
  }, []);

  const handleReject = useCallback(
    async (rejectReason) => {
      try {
        setStatus(null);
        handleCloseModal();
        setLoading(true);
        await postRejectDocument({
          docId,
          rejectReason,
          docType: selectedDocType.value,
          uuid: taskDetail?.carrier?.uuid,
          document_type_name: taskDetail?.document_type?.label,
          notify_carrier: notifyCarrier,
          entity_type: taskDetail?.metadata?.entity_type,
          entity_uuid: taskDetail?.metadata?.uuid,
        });
        setStatus('warning');
      } catch (err) {
        console.error(err);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    },
    [docId, postRejectDocument, handleCloseModal, selectedDocType, taskDetail, notifyCarrier]
  );

  const handleValidate = useCallback(async () => {
    try {
      setStatus(null);
      setErrorMessage(null);
      setLoading(true);
      await postValidateDocument({
        docId,
        expiration_date:
          taskDetail.workflow !== 'validation_without_expiration'
            ? moment(selectedDate).format()
            : '',
        iban_number:
          taskDetail.document_type.label === bankOwnershipCertificateLabel
            ? ibanNumber.replace(/\s/g, '')
            : '',
        insurance_coverage_amount:
          taskDetail.document_type.label === insuranceLabel ? parseFloat(insuranceCoverage) : 0.0,
        docTypeId: selectedDocType.value,
        uuid: taskDetail?.carrier?.uuid,
        workflow: taskDetail.workflow,
        document_type_name: taskDetail?.document_type?.label,
        notify_carrier: notifyCarrier,
        entity_type: taskDetail?.metadata?.entity_type,
        entity_uuid: taskDetail?.metadata?.uuid,
      });
      setStatus('success');
    } catch (err) {
      const message = await http.getError(err);
      console.error(message);
      setErrorMessage(message);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [
    docId,
    selectedDate,
    ibanNumber,
    insuranceCoverage,
    selectedDocType,
    postValidateDocument,
    taskDetail,
    notifyCarrier,
  ]);

  const handleCloseDetail = useCallback(() => {
    setStatus(null);
    history.push('/docs/');
  }, [history]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleIbanChange = (iban) => {
    setIbanNumber(iban);
  };

  const handleInsuranceCoverageChange = (coverage) => {
    setInsuranceCoverage(coverage);
  };

  const checkDataIsValid = useCallback(() => {
    if (taskDetail.workflow === 'validation_with_expiration') {
      return checkDateIsFromFuture(selectedDate) && selectedDate !== null;
    } else if (taskDetail.document_type.label === bankOwnershipCertificateLabel) {
      return ibanNumber !== '';
    } else if (taskDetail.document_type.label === insuranceLabel) {
      return insuranceCoverage && insuranceCoverage !== '';
    } else {
      return true;
    }
  }, [taskDetail, ibanNumber, insuranceCoverage, selectedDate, checkDateIsFromFuture]);

  const validateData = useCallback(() => {
    setValidationError(selectedDocType === null || !checkDataIsValid());
  }, [selectedDocType, checkDataIsValid]);

  useEffect(() => {
    if (
      !loadingTaskDetail &&
      !error &&
      docId &&
      (!taskDetail || taskDetail.id !== parseInt(docId))
    ) {
      setStatus(null);
      fetchTaskDetail(docId);
    }
    return () => {
      /*noop*/
    };
  }, [fetchTaskDetail, loadingTaskDetail, error, docId, taskDetail]);

  useEffect(() => {
    if (!documentTypes && !loadingDocTypes && !error) {
      fetchDocumentTypes();
    }
  }, [fetchDocumentTypes, documentTypes, loadingDocTypes, error]);

  useEffect(() => {
    if (documentTypes && docTypeOptions.length === 0) {
      generateOptions();
    }
  }, [documentTypes, docTypeOptions, generateOptions]);

  useEffect(() => {
    if (taskDetail) {
      generateOptions();
    }
  }, [taskDetail, generateOptions]);

  useEffect(() => {
    if (docTypeOptions.length > 0 && taskDetail) {
      setSelectedDocType(
        docTypeOptions.find((docType) => docType.value === taskDetail.document_type.id) || null
      );
    }
  }, [taskDetail, docTypeOptions]);

  useEffect(() => {
    if (taskDetail) {
      validateData();
    }
  }, [taskDetail, validateData]);

  useEffect(() => {
    if (selectedDate && selectedDocType) {
      validateData();
    }
  }, [selectedDate, selectedDocType, validateData]);

  useEffect(() => {
    resetState();
    setStatus(null);
    setSelectedDate(null);
    setIbanNumber(null);
    setNotifyCarrier(true);
    setPreviewError(false);
    setInsuranceCoverage(null);
  }, [docId, resetState]);

  if (loadingTaskDetail) {
    return (
      <div className={className}>
        <div className="docs-task-detail">
          <div className="docs-task-detail__loading" data-testid="docs-task-detail__loading">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <div className="docs-task-detail">
          <div className="docs-task-detail__error" data-testid="docs-task-detail__error">
            {t.error}
          </div>
        </div>
      </div>
    );
  }

  if (!taskDetail) return null;

  if (status) {
    return (
      <div className={className}>
        <div className="docs-task-detail">
          <DocsAlert
            carrierName={taskDetail.carrier.contact_name}
            errorMessage={errorMessage}
            docType={taskDetail.document_type.label}
            skin={status}
            onClose={handleCloseDetail}
          />
        </div>
      </div>
    );
  }

  const isOwner = taskDetail.metadata?.owner === me.email;
  return (
    <div className={className}>
      <div className="docs-task-detail" data-testid="docs-task-detail">
        <div className="docs-task-detail__document-container">
          <DocsCarrierInfo carrier={taskDetail.carrier} />
          <div className="docs-task-detail__document-actions">
            <div className="docs-task-detail__actions-container">
              <div
                className="docs-task-detail__select-container"
                data-testid="docs-task-detail__select-container"
              >
                <label className="docs-task-detail__select-label">{t.docType}</label>
                <Select
                  className="docs-task-detail__actions-select"
                  value={selectedDocType}
                  onChange={handleChangeDocType}
                  options={docTypeOptions}
                  styles={{
                    ...reactSelectStyles,
                    indicatorSeparator: () => {
                      /*noop*/
                    },
                  }}
                  error={validationError}
                />
              </div>
              {taskDetail.workflow !== 'validation_without_expiration' && (
                <div className="docs-task-detail__date-container">
                  <DateField
                    label={t.validUntil}
                    value={selectedDate}
                    onChange={handleDateChange}
                    inputClass="docs-task-detail__actions-date"
                    size="M"
                    error={validationError ? t.dateError : ''}
                  />
                </div>
              )}
              {taskDetail.workflow === 'validation_without_expiration' &&
                taskDetail.document_type.label === bankOwnershipCertificateLabel && (
                  <div
                    className="docs-task-detail__iban-container"
                    data-testid="docs-task-detail__iban-container"
                  >
                    <Field
                      label="IBAN number"
                      id="IBAN number"
                      name="IBAN number"
                      className="docs-task-detail__iban-field"
                      size="M"
                      value={ibanNumber ? ibanNumber.toUpperCase() : ''}
                      onChange={(e, { value }) => handleIbanChange(value)}
                      error={!ibanNumber}
                    />
                  </div>
                )}
              {taskDetail.workflow === 'validation_without_expiration' &&
                taskDetail.document_type.label === insuranceLabel && (
                  <div
                    className="docs-task-detail__insurance-coverage"
                    data-testid="docs-task-detail__insurance-coverage"
                  >
                    <Field
                      className="docs-task-detail__insurance-coverage-field"
                      size="M"
                      label=""
                      type="number"
                      id="Insurance coverage"
                      name="Insurance coverage"
                      asideRightText={intl.messages.currencies[taskDetail.carrier.currency]}
                      value={insuranceCoverage ? insuranceCoverage : ''}
                      onChange={(e, { value }) => handleInsuranceCoverageChange(value)}
                      labelType="hidden"
                      placeholder={'Enter the total amount'}
                      error={!insuranceCoverage}
                    />
                  </div>
                )}
            </div>
            <div className="docs-task-detail__actions-container docs-task-detail__actions-container--buttons">
              <div className="docs-task-detail__notify-container">
                <Checkbox
                  label={t.notifyCarrier}
                  size="M"
                  checked={notifyCarrier}
                  onChange={(e, { value }) => setNotifyCarrier(value)}
                />
              </div>
              <div className="docs-task-detail__buttons-container">
                {!isOwner && (
                  <span className="docs-task-detail__warning-alert">
                    <Icon glyph="girded-exclamation" size="1.6rem" />
                    {t.notOwner}
                  </span>
                )}
                <div>
                  <Button
                    className="docs-task-detail__actions-button"
                    data-testid="docs-task-detail__reject-button"
                    text={t.discard}
                    ghost
                    skin="primary"
                    onClick={handleShowModal}
                    disabled={!isOwner}
                  />
                  <Button
                    className="docs-task-detail__actions-button"
                    data-testid="docs-task-detail__validate-button"
                    text={t.validate}
                    skin="primary"
                    disabled={validationError || loading || !isOwner}
                    onClick={handleValidate}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="docs-task-detail__doc-viewer-actions">
            <div className="docs-task-detail__metadata" data-testid="docs-task-detail-metadata">
              {taskDetail?.metadata?.plate_number && (
                <div
                  className="docs-task-detail__metadata-text"
                  data-testid="docs-task-detail-metadata-text"
                >
                  {`${t.plateNumber}: ${taskDetail?.metadata?.plate_number}`}{' '}
                  {`${t.status}: ${taskDetail?.metadata?.status}`}
                </div>
              )}
              {taskDetail?.metadata?.full_name && (
                <div className="docs-task-detail__metadata-text">
                  {`${t.full_name}: ${taskDetail?.metadata?.full_name}`}{' '}
                  {`${t.status}: ${taskDetail?.metadata?.status}`}
                </div>
              )}
            </div>
            <div
              className="docs-task-detail__metadata-page-count"
              data-testid="docs-task-detail-metadata-page-count"
            >
              {taskDetail?.number_of_pages > 0 && (
                <div className="docs-task-detail__metadata-text">
                  {t.number_of_pages}: 1 / {taskDetail.number_of_pages}
                </div>
              )}
            </div>
            <Button
              nude
              skin="primary"
              className="docs-task-detail__actions-button"
              onClick={() =>
                window.open(`${config.BASE_URL}/api/dms/download/${taskDetail.doc_uuid}/`)
              }
            >
              <DownloadIcon />
            </Button>
          </div>
          {taskDetail && (
            <div className="docs-task-detail__viewer-container">
              {!previewError && (
                <img
                  className="docs-task-detail__viewer"
                  src={`${config.BASE_URL}/api/dms/preview/${taskDetail.id}/?doc_uuid=${taskDetail.doc_uuid}`}
                  alt={t.previewDoc}
                  onError={handleImageError}
                />
              )}
              {previewError && <Text text={t.previewError} />}
            </div>
          )}
        </div>
        <DocsRejectModal
          show={showModal}
          onClose={handleCloseModal}
          onSave={handleReject}
          loading={loading}
        />
      </div>
    </div>
  );
};

DocsTaskDetail.defaultProps = {
  className: '',
};

DocsTaskDetail.propTypes = {
  intl: PropTypes.any,
  className: PropTypes.string,
};

export default injectIntl(DocsTaskDetail);
